/**
 * Admin panel: Single Sign-On (Microsoft 365 / Entra ID) - platform-wide configuration.
 * Backend: /api/m365-sso/admin/* (secret is write-only, encrypted at rest, never returned).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  KeyRound, Loader2, Save, PlugZap, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, Copy, Plus, X, Info,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const EMPTY = {
  application_name: '', client_id: '', tenant_id: '', redirect_uri: '', secret_expiry_date: '',
  app_owners: [], apm_number: '', tag_detail: '', default_org_id: 'org_navigate_master', default_role: 'learner',
};

const EXPIRY_STYLES = {
  ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  warning: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  critical: 'border-orange-500/50 bg-orange-500/15 text-orange-300',
  expired: 'border-red-500/50 bg-red-500/15 text-red-300',
  unknown: 'border-zinc-700 bg-zinc-800/40 text-zinc-400',
};

function Field({ id, label, required, children, help }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-zinc-300">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</Label>
      {children}
      {help && <p className="text-xs text-zinc-500">{help}</p>}
    </div>
  );
}

export default function M365SSOSettings({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [secret, setSecret] = useState('');
  const [replacingSecret, setReplacingSecret] = useState(false);
  const [ownerDraft, setOwnerDraft] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [roles, setRoles] = useState(['learner']);
  const [errors, setErrors] = useState([]);
  const [lastTest, setLastTest] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/m365-sso/admin/config`, { headers });
      const c = res.data.config || {};
      setConfig(c);
      setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, c[k] ?? EMPTY[k]])) });
      setOrgs(res.data.organizations || []);
      setRoles(res.data.roles || ['learner']);
      setLastTest(c.last_test || null);
      setReplacingSecret(!c.secret_set);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SSO settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addOwner = () => {
    const v = ownerDraft.trim();
    if (!v) return;
    if (!form.app_owners.includes(v)) set('app_owners', [...form.app_owners, v]);
    setOwnerDraft('');
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const body = { ...form, secret_expiry_date: form.secret_expiry_date || null };
      if (replacingSecret && secret) body.secret_value = secret;
      const res = await axios.put(`${API}/m365-sso/admin/config`, body, { headers });
      setConfig(res.data.config);
      setSecret('');
      setReplacingSecret(!res.data.config.secret_set);
      if (res.data.notice) toast.warning(res.data.notice);
      toast.success('SSO settings saved');
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) setErrors(d.errors);
      toast.error(d?.message || d || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/m365-sso/admin/test`, {}, { headers });
      setLastTest(res.data);
      if (res.data.success) toast.success('Connection test passed');
      else toast.error('Connection test failed - see details below');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed to run');
    } finally {
      setTesting(false);
    }
  };

  const toggle = async (enabled) => {
    setToggling(true);
    setErrors([]);
    try {
      await axios.post(`${API}/m365-sso/admin/enable`, { enabled }, { headers });
      toast.success(enabled ? 'Microsoft 365 sign-in enabled' : 'Microsoft 365 sign-in disabled');
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) setErrors(d.errors);
      toast.error(d?.message || d || 'Could not change SSO state');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-navigate-gold" /></div>;
  }

  const expiry = config?.expiry || { level: 'unknown', message: 'Secret expiry date not set' };
  const enabled = !!config?.enabled;
  const testIsCurrent = !!config?.last_test_current;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="m365-sso-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-navigate-gold" /> Single Sign-On
          </h2>
          <p className="text-sm text-zinc-400 mt-1">Microsoft 365 (Entra ID) sign-in using OpenID Connect. Only accounts from the configured tenant can sign in.</p>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-xl border border-zinc-800 bg-navigate-surface">
          {enabled ? <ShieldCheck className="h-5 w-5 text-emerald-400" /> : <ShieldAlert className="h-5 w-5 text-zinc-500" />}
          <div className="text-sm">
            <div className="text-white font-medium">Microsoft 365 sign-in</div>
            <div className={enabled ? 'text-emerald-400 text-xs' : 'text-zinc-500 text-xs'} data-testid="sso-state-label">{enabled ? 'Enabled - button shown on login page' : 'Disabled - normal login only'}</div>
          </div>
          <Switch checked={enabled} disabled={toggling} onCheckedChange={toggle} data-testid="sso-enable-toggle" />
        </div>
      </div>

      {/* Secret expiry warning (30 / 7 days) */}
      <div className={`flex items-start gap-3 p-4 rounded-xl border ${EXPIRY_STYLES[expiry.level] || EXPIRY_STYLES.unknown}`} data-testid="sso-expiry-banner" data-level={expiry.level}>
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium">Client secret {expiry.level === 'ok' ? 'status' : expiry.level === 'unknown' ? 'expiry not set' : 'expiry warning'}</div>
          <div className="opacity-90">{expiry.message}</div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="sso-errors">
          <ul className="list-disc pl-5 space-y-1">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      <section className="p-5 rounded-xl border border-zinc-800 bg-navigate-surface space-y-5">
        <h3 className="text-white font-semibold">Azure app registration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="sso-app-name" label="Application Name" required>
            <Input id="sso-app-name" value={form.application_name} onChange={(e) => set('application_name', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="sso-application-name" />
          </Field>
          <Field id="sso-client-id" label="Application (Client) ID" required help="Azure > App registrations > your app > Overview">
            <Input id="sso-client-id" value={form.client_id} onChange={(e) => set('client_id', e.target.value.trim())} placeholder="00000000-0000-0000-0000-000000000000" className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm" data-testid="sso-client-id" />
          </Field>
          <Field id="sso-tenant-id" label="Directory (Tenant) ID" required help="Sign-in is restricted to this tenant only">
            <Input id="sso-tenant-id" value={form.tenant_id} onChange={(e) => set('tenant_id', e.target.value.trim())} placeholder="00000000-0000-0000-0000-000000000000" className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm" data-testid="sso-tenant-id" />
          </Field>
          <Field id="sso-redirect" label="Redirect URL (Web) (optional)" help={<span>Leave blank if not provided (N/A). This site's callback <code className="text-navigate-gold">{config?.expected_redirect_uri}</code> is used automatically.</span>}>
            <div className="flex gap-2">
              <Input id="sso-redirect" placeholder="N/A, leave blank to use this site's callback" value={form.redirect_uri} onChange={(e) => set('redirect_uri', e.target.value.trim())} className="bg-zinc-900 border-zinc-700 text-white font-mono text-xs" data-testid="sso-redirect-uri" />
              <Button type="button" variant="outline" size="sm" className="border-zinc-700 text-zinc-300 shrink-0" onClick={() => set('redirect_uri', config?.expected_redirect_uri || '')} data-testid="sso-use-expected-redirect">Use this site</Button>
            </div>
          </Field>
          <Field id="sso-secret" label="Secret Value" required help="Write-only. Stored encrypted; never shown again after saving.">
            {config?.secret_set && !replacingSecret ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 h-10 px-3 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-400 font-mono text-sm flex items-center" data-testid="sso-secret-masked">{config.secret_hint || '••••••••'}</div>
                <Button type="button" variant="outline" size="sm" className="border-zinc-700 text-zinc-300" onClick={() => setReplacingSecret(true)} data-testid="sso-replace-secret-btn">Replace</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input id="sso-secret" type="password" autoComplete="new-password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Paste the secret VALUE (not the secret ID)" className="bg-zinc-900 border-zinc-700 text-white" data-testid="sso-secret-input" />
                {config?.secret_set && <Button type="button" variant="ghost" size="sm" className="text-zinc-400" onClick={() => { setReplacingSecret(false); setSecret(''); }}>Cancel</Button>}
              </div>
            )}
          </Field>
          <Field id="sso-expiry" label="Secret Expiry Date" required help="Warnings appear 30 and 7 days before expiry">
            <Input id="sso-expiry" type="date" value={form.secret_expiry_date || ''} onChange={(e) => set('secret_expiry_date', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="sso-secret-expiry" />
          </Field>
        </div>
      </section>

      <section className="p-5 rounded-xl border border-zinc-800 bg-navigate-surface space-y-5">
        <h3 className="text-white font-semibold">Governance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="sso-owners" label="App Owners" help="Add one or more owners (names or emails)">
            <div className="flex gap-2">
              <Input id="sso-owners" value={ownerDraft} onChange={(e) => setOwnerDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOwner(); } }} className="bg-zinc-900 border-zinc-700 text-white" data-testid="sso-owner-input" />
              <Button type="button" variant="outline" size="sm" className="border-zinc-700 text-zinc-300" onClick={addOwner} data-testid="sso-owner-add"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.app_owners.map((o) => (
                <span key={o} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-zinc-800 text-zinc-200" data-testid="sso-owner-chip">
                  {o}<button type="button" onClick={() => set('app_owners', form.app_owners.filter((x) => x !== o))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </Field>
          <Field id="sso-apm" label="APM Number">
            <Input id="sso-apm" value={form.apm_number} onChange={(e) => set('apm_number', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="sso-apm-number" />
          </Field>
          <div className="md:col-span-2">
            <Field id="sso-tag" label="TAG Detail">
              <textarea id="sso-tag" rows={2} value={form.tag_detail} onChange={(e) => set('tag_detail', e.target.value)} className="w-full rounded-md bg-zinc-900 border border-zinc-700 text-white text-sm p-2" data-testid="sso-tag-detail" />
            </Field>
          </div>
        </div>
      </section>

      <section className="p-5 rounded-xl border border-zinc-800 bg-navigate-surface space-y-4">
        <h3 className="text-white font-semibold">First-time SSO users</h3>
        <p className="text-xs text-zinc-500">New accounts are created automatically from the Microsoft token (name + email) and placed here.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="sso-default-org" label="Default organisation">
            <select id="sso-default-org" value={form.default_org_id || ''} onChange={(e) => set('default_org_id', e.target.value)} className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="sso-default-org">
              {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
            </select>
          </Field>
          <Field id="sso-default-role" label="Default role">
            <select id="sso-default-role" value={form.default_role} onChange={(e) => set('default_role', e.target.value)} className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm capitalize" data-testid="sso-default-role">
              {roles.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
          </Field>
        </div>
      </section>

      <div className="flex flex-wrap gap-3">
        <Button onClick={save} disabled={saving} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="sso-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </Button>
        <Button onClick={test} disabled={testing || !config?.configured} variant="outline" className="border-zinc-700 text-zinc-200" data-testid="sso-test-btn">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test Connection
        </Button>
        {!config?.configured && <span className="text-xs text-zinc-500 self-center">Save all required fields to enable testing.</span>}
      </div>

      {lastTest && (
        <section className="p-5 rounded-xl border border-zinc-800 bg-navigate-surface" data-testid="sso-test-results">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-white font-semibold flex items-center gap-2">
              {lastTest.success ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
              Last connection test: {lastTest.success ? 'passed' : 'failed'}
            </h3>
            <span className="text-xs text-zinc-500">{lastTest.at && new Date(lastTest.at).toLocaleString()} {lastTest.by && `by ${lastTest.by}`} {!testIsCurrent && <span className="text-amber-400 ml-2">(settings changed since - test again)</span>}</span>
          </div>
          <ul className="space-y-2">
            {(lastTest.checks || []).map((c) => (
              <li key={c.check} className="flex items-start gap-2 text-sm" data-testid={`sso-check-${c.check}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> : (c.warning ? <Info className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />)}
                <span className={c.ok ? 'text-zinc-300' : 'text-zinc-200'}><span className="text-zinc-500 mr-1">{c.label || c.check}:</span>{c.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="p-4 rounded-xl border border-zinc-800 text-xs text-zinc-400 flex items-start gap-2">
        <Copy className="h-4 w-4 shrink-0" />
        <span>Sign-ins and failures are recorded in the Audit Log. Disabling SSO never affects the normal login.</span>
      </div>
    </div>
  );
}
